import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = {
  name: "Titan Car Rentals",
  legalName: "Titan Car Rentals — Ghana",
  url: "https://titancarrental.com",
  logo: "https://titancarrental.com/titan-logo.png",
  phone: "+233540124247",
  altPhone: "+233201234999",
  whatsapp: "+233244945582",
  email: "info@titancarrental.com",
  address: {
    street: "3 Maseru Street, East Legon",
    city: "Accra",
    region: "Greater Accra",
    country: "GH",
  },
  geo: { lat: 5.6308, lng: -0.1535 },
  social: [
    "https://www.instagram.com/titancarrentalsgh",
    "https://www.facebook.com/titancarrentalsgh",
    "https://www.tiktok.com/@titancarrentalsgh",
  ],
};

const DEFAULT_TITLE =
  "Titan Car Rentals — Best Car Rentals in Ghana | Accra Car Hire";
const DEFAULT_DESC =
  "Titan Car Rentals — the best car rental service in Ghana. Daily, long-term, chauffeur, airport pickup. Sedans, SUVs, Land Cruisers. East Legon, Accra. From GH₵600/day.";
const DEFAULT_OG =
  "https://customer-assets.emergentagent.com/job_titan-drive/artifacts/titan-og.jpg";

export default function SEO({
  title,
  description,
  path = "/",
  image,
  keywords,
  noindex = false,
  jsonLd,
  type = "website",
}) {
  const fullTitle = title ? `${title} | ${SITE.name}` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const canonical = `${SITE.url}${path}`;
  const ogImage = image || DEFAULT_OG;
  const kw =
    keywords ||
    "car rental Ghana, car hire Accra, best car rentals Ghana, premium car rental Ghana, rent a car Accra, East Legon car rental, airport car hire Ghana, Land Cruiser rental Ghana, chauffeur Ghana";

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GH" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Geo */}
      <meta name="geo.region" content="GH-AA" />
      <meta name="geo.placename" content="Accra" />
      <meta name="geo.position" content={`${SITE.geo.lat};${SITE.geo.lng}`} />
      <meta name="ICBM" content={`${SITE.geo.lat}, ${SITE.geo.lng}`} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// -------- Reusable schema builders --------

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AutoRental",
  "@id": `${SITE.url}/#organization`,
  name: SITE.name,
  alternateName: ["Titan Car Rentals Ghana", "Titan Car Rental", "Titan Rentals"],
  url: SITE.url,
  logo: SITE.logo,
  image: SITE.logo,
  telephone: SITE.phone,
  email: SITE.email,
  priceRange: "GH₵600 – GH₵2,800 per day",
  currenciesAccepted: "GHS",
  paymentAccepted: "Mobile Money, Bank Transfer, Cash",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.region,
    addressCountry: SITE.address.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: SITE.geo.lat,
    longitude: SITE.geo.lng,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "07:00",
      closes: "20:00",
    },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "09:00", closes: "18:00" },
  ],
  sameAs: SITE.social,
  areaServed: [
    { "@type": "City", name: "Accra" },
    { "@type": "City", name: "Tema" },
    { "@type": "City", name: "Kumasi" },
    { "@type": "City", name: "Takoradi" },
    { "@type": "Country", name: "Ghana" },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Car rental fleet",
    itemListElement: [
      { "@type": "Offer", name: "Sedan", price: "600", priceCurrency: "GHS" },
      { "@type": "Offer", name: "Mid-size SUV", price: "1000", priceCurrency: "GHS" },
      { "@type": "Offer", name: "Toyota Prado", price: "1800", priceCurrency: "GHS" },
      { "@type": "Offer", name: "Toyota Land Cruiser", price: "2800", priceCurrency: "GHS" },
    ],
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "142",
    bestRating: "5",
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  url: SITE.url,
  name: SITE.name,
  publisher: { "@id": `${SITE.url}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE.url}/fleet?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE.url}${it.path}`,
    })),
  };
}

export function faqSchema(qa) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

/**
 * Standalone Article schema for the top FAQ entries.
 * Lets answer engines (Google AI Overviews, Perplexity, ChatGPT) lift these
 * as standalone snippets rather than only as part of the FAQ block.
 */
export function articleSchema({ headline, body, path = "/faq", datePublished = "2026-02-01" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    articleBody: body,
    inLanguage: "en-GH",
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.url}${path}` },
    author: { "@id": `${SITE.url}/#organization` },
    publisher: { "@id": `${SITE.url}/#organization` },
    datePublished,
    dateModified: datePublished,
    image: `${SITE.url}/og-image.png`,
  };
}

export function carSchema(car) {
  const cleanName = String(car.name || "")
    .replace(/\s*\(\s*(?:19|20)\d{2}(?:\s*[&\-/,]\s*(?:19|20)\d{2})*\s*\)/g, "")
    .replace(/\s+(?:19|20)\d{2}(?=\s|$)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  const full = `${car.brand} ${cleanName}`.trim();
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: full,
    image: car.image_url,
    description: `${full} for hire in Accra, Ghana. Self-drive or with chauffeur. Daily, weekly and long-term rates.`,
    brand: { "@type": "Brand", name: car.brand },
    category: car.category,
    offers: {
      "@type": "Offer",
      price: car.daily_rate,
      priceCurrency: "GHS",
      availability: car.available
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      seller: { "@id": `${SITE.url}/#organization` },
      url: `${SITE.url}/cars/${car.id}`,
    },
  };
}

export { SITE };
